<script setup lang="ts">
import CardProcessingDialog from "@/components/dialogs/CardProcessingDialog.vue";
import { usePaymentStore } from "@/components/payment/usePaymentStore";
import { useBooking as useBookingStore } from "@/store/useBooking";
import { useCalendarStore } from "@/store/useCalendarStore";
import { useDiscountCode } from "@/store/useDiscountCode";
import { Round2String, getCardProviders } from "@/utils/Formatting";
import { useLocationStore } from "@/utils/LocationStore";
import { pb } from "@/utils/PocketBaseAdapter";
import { useUserStore } from "@/utils/UserStore";
import { useUserRole } from "@/utils/useUserRole";
import {
  creditLimitValidator,
  customRegexValidator,
  decimalRule,
  minMaxValidator,
  minValidator,
  requiredValidator,
} from "@validators";
import isEmpty from "lodash/isEmpty";
import { toast } from "vue3-toastify";
import type { Emit, Props, Tab } from "./types";

// props
const props = defineProps<Props>();

// emit
const emit = defineEmits<Emit>();

// store
const usePayment = usePaymentStore();
const discount = useDiscountCode();
const userStore = useUserStore();
const bookingStore = useBookingStore();
const locationStore = useLocationStore();
const calenderStore = useCalendarStore();
const roleStore = useUserRole();

const {
  getCouponList,
  payWithGiftCardOrCoupon,
  initiateDocketForBooking,
  handleUserCreditAndWaiveFeePayment,
  payWithAutoApplyCoupons,
  createEditDocketForCustomizePurchase,
  payByUserCredit,
  payByGiftCard,
  payByPos,
  eventDuePayByPos,
  eventDuePayByWaiveFee,
  initiateDocketForEvent,
  processBooking
} = usePayment;

const showPreventBenefits = ref(false)

const usersForCouponsList = ref([])

const getUsersForCouponsList = () => {
  let items:any = []
  usersForCouponsList.value.forEach((u:any) => {
    let title = (u.firstName + u.lastName).trim() != '' ? u.firstName + ' ' + u.lastName : u.username
    items.push({value:u.id,title, props: {subtitle: u.username}})
  })

  return items
}

const barcodeReaderID = ref('')
const doBarcodeReaderSubscribe = () => {
  try {
    let appscheck:any = localStorage.getItem('Apps');
    if(appscheck) {
      let json = JSON.parse(appscheck)
      if (json?.POS?.barcodeReader && json?.POS?.isVisible) {
        barcodeReaderID.value = json.POS.barcodeReader
        pb.collection('barcodeReaders').subscribe(barcodeReaderID.value, async (result: {
          action: string,
          record: any
        }) => {
          if (result?.action == 'update' && result?.record?.lastType == "Gift Card") {
            giftCardId.value = result?.record?.lastRead
            await validateGiftCard()
          }
        })
      }
    }
  }catch(e){}
}

onUnmounted(()=>{
  localStorage.removeItem('onpayment')
  if(barcodeReaderID.value && barcodeReaderID.value != '') {
    pb.collection("barcodeReaders").unsubscribe(barcodeReaderID.value)
    barcodeReaderID.value = '';
  }
})

import { useRouter } from "vue-router";
const router = useRouter()
const docketBookingId = ref('')
const docketTotalDue = ref(0)
import AddToMyTabDialog from "../dialogs/AddToMyTabDialog.vue";
import {moment} from "@/utils/useTimeZone";
const isAddToMyTabDialogVisible = ref(false)
const addToDocket = (allProps:any) => {
  // 1.0 Show Docket List
  docketBookingId.value = allProps.bookingId
  docketTotalDue.value = allProps.totalDue
  isAddToMyTabDialogVisible.value = true
  //    1.1 Allow user to select a docket
  //    1.2 Add a docketLine Item to the docket for this booking
  // 2.0 Add Booking to Docket
  //    2.1  Add Products to Docket
  // 3.0 Take the user to the POS page
}

const canPayAtPos = () => {
  try {
    let apps = localStorage.getItem("Apps")
    if (apps) {
      let jsonApps = JSON.parse(apps);
      if (jsonApps?.POS?.isVisible) {
        return true
      }
    }
  } catch (e) {
  }
  return false
}

const payEventOnDocket = async (params:any,docketId:any) => {
  const { eventId, loggedInUser, activityMap, due } = params;
  const eventData = await pb.collection("events").getOne(eventId, {
    expand: "bookings",
  });

  for (const booking of eventData?.expand?.bookings || []) {
    // Process the booking and add docketLines under the same docket
    await processBooking(
        booking,
        docketId,
        loggedInUser,
        activityMap,
    );
  }
}

const handleAddToMyTab = async (val:any) => {
  // check the type
  if(props?.type === "event"){
    const details = {
      eventId: props?.eventId,
      loggedInUser: props?.loggedInUserId,
      activityMap: props?.activityMap,
      due: props?.totalDue,
      location: props?.location,
      owner: props?.owner,
    } as any;

    await payEventOnDocket(details, val)

    // Close the Dialog
    emit("closeDialog", false);
    // 3.0 Take the user to the POS page
    await router.push({
      name: "pos",
      query: {
        did: val,
      },
    });
    return
  }

  // 1.1 Remove Booking From Other Dockets and other Docket Lines
  // Remove this Booking From Other DocketLines
  let oldDocketId = ''
  try {
    let previousLines = await pb.collection("docketLines").getFullList({filter: 'booking="' + docketBookingId.value + '"'})
    if (previousLines) {
      for (const line of previousLines) {
        oldDocketId = line.docket
        await pb.collection("docketLines").delete(line.id)
      }

      if(oldDocketId != ''){
        let oldDocketsLine = await pb.collection("docketLines").getFullList({filter: 'docket="' + oldDocketId + '" && product.hiredOutItem=true', expand:'product'})
        if(oldDocketsLine){
          for (const oldDocketLine of oldDocketsLine) {
            await pb.collection("docketLines").delete(oldDocketLine.id)
          }
        }
      }
    }
  } catch (e) {}

  // Remove This Booking Option from Other Docket Lines

  // hiredOutItem

  // 2.0 Add Booking to DocketLine
  const totalAmount = props?.bookingAmount || 0; // total amount before discount
  const discountRate = props?.discountBookings || 0; // percentage discount
  const discountProducts = props?.discMemProducts || 0

  // Calculate the total discount amount and due amount after payment
  const discountAmount = (totalAmount * discountRate) / 100;
  const due = totalAmount - discountAmount - (props?.totalPaid || 0);

  // TODO - GET THE PRICE OF THE BOOKING AND THE QUANTITY HERE
  let sessionPrice = totalAmount;
  try {
    let durationCheck = await pb.collection('bookings')
      .getOne(docketBookingId.value, {expand:'activity'})
    if(durationCheck && durationCheck?.expand?.activity?.sessionFullPrice){
      sessionPrice = Math.floor(durationCheck.expand.activity.sessionFullPrice)
    }
  } catch(e){}

  let quantity = Math.floor(totalAmount / sessionPrice)
  if(quantity < 1){
    quantity = 1
  }

  const data = {
    docket: val,
    transactionType: "Booking Payment",
    qty: quantity,
    itemPrice: sessionPrice,          // Booking Price to Go Here
    discount: discountRate,           // Booking Discount to Go Here
    lineTotal: totalAmount - discountAmount,
    createdBy: userId.value,
    booking: docketBookingId.value,   // Booking ID to Go Here
    posDevice: localStorage.getItem("POS_SETUP") || "" // POS DEVICE to Go Here
  };

  try {
    let DocketLine = await pb.collection("docketLines").create(data); // Creating New Line
    // FEEDBACK - Add Docket Line To Booking
    let getBookingDetails = await pb.collection("bookings").getOne(docketBookingId.value)
    if(getBookingDetails && getBookingDetails.state != 'Confirmed') {
      let PersontoEmail = getBookingDetails?.users && getBookingDetails?.users.length > 0 ? getBookingDetails.users[0] : getBookingDetails.owner
      try {
        let sendQue = {
          sendVia: 'email',
          type: 'Booking',
          status: 'New',
          recordID: docketBookingId.value,
          user: PersontoEmail
        }
        await pb.collection("sendQueue").create(sendQue)
      } catch (e) {
      }
    }
    let BookingDetails = await pb.collection("bookings").update(docketBookingId.value, {docketLine: DocketLine.id, state:"Confirmed"});

    // Update Main Docket
    let docketUpdate = await pb.collection("dockets").update(val,
      {
        discountSources: props?.discountSources,
        discountEvents: props?.discountEvents,
        discountProducts: props.discountProduct,
        user: BookingDetails.users[0],
      });

    // Add products
    if (props.products && props.products.length > 0) {
      for (const product of props.products) {

        if (product) {
          // Delete Other Item first
          const productDocketLineData = {
            docket: val,
            transactionType: "Product",
            product: product.id,
            qty: product.qty || 1,
            itemPrice: product.sellPrice,
            discount: discountProducts,
            lineTotal: (product.qty || 1) * product.itemPrice,
            status: "Available",
            createdBy: userId.value,
          };

          // Create a new docket line for each product
          await pb
            .collection("docketLines")
            .create(productDocketLineData);
        }
      }
    }

    // Close the Dialog
    emit("closeDialog", false);
    // 3.0 Take the user to the POS page
    router.push({
      name: "pos",
      query: {
        did: val,
      },
    });
  } catch (e) {
    console.log(e)
    console.log("Cannot Create Docket")
  }
}

const { handleDiscountCodeApply, validateBookingDateAppliedDiscountCode } = discount;
const { apps } = storeToRefs(roleStore);

const { couponList, docketId, docketLineId, lowBalanceError } =
  storeToRefs(usePayment);

const { addCardDialogMessage, isProcessingCardDialogVisible } =
  storeToRefs(bookingStore);

const { subscribeBookingPayment } = bookingStore;

const { currentLocationDetails, currentLocation, userId } =
  storeToRefs(userStore);

const { locationList } = storeToRefs(locationStore);
const { usersList } = storeToRefs(calenderStore);

// Form Refs
const refVFormCoupon = ref<any>("");
const refVFormGiftCard = ref<any>("");
const refVFormWaive = ref<any>();
const refVFormCreditCard = ref<any>();
const refCreditAmtForm = ref<any>();

// Data
const selectedUser = ref<any>();
const quantity = ref<any>("");
const selectedCoupon = ref<any>();
const giftCardId = ref<string>("");
const appliedGiftCardDetails = ref<any>("");
const isInvalidCoupon = ref<boolean>(false);
const amountToRedeem = ref<number>();
const feeWaiveReason = ref<string>("");
const isTooltipVisible = ref<boolean>(false);
const selectedCreditCard = ref<any>('newCard');
const saveNewCard = ref<boolean>(true);
const payingDocket = ref<string | undefined>();
// computed
const getuserCreditAmt = computed(() => {
  // Use find instead of filter
  const userFound = usersList.value?.find((user) => user.id === props?.owner);
  if (!userFound) return 0;

  return {
    paying: Math.min(userFound.tempBalance, props.totalDue),
    originalAmt: userFound.tempBalance,
  };
});

const creditAmtTopay = ref<number>(getuserCreditAmt.value.paying ?? 0);

const getCoupons = computed(() => {
  let bookingDate = moment(props?.bookingProp?.startTime);

  return (
    couponList.value?.filter((coupon: any) => {
      let dc = moment(coupon?.validFrom)
      let dcEnd = moment(coupon?.expiryTime)
      return coupon.user === couponUser.value && (props?.loggedInUserId || userId?.value)
        && (!coupon?.validFrom || coupon?.validFrom == "" || bookingDate.isSameOrAfter(dc))
       && bookingDate.isSameOrBefore(dcEnd)
    }) || []);
});

const tabs = computed<Tab[]>(() => {
  let tabsList = [
    {
      value: "pos",
      label: "Pos",
      icon: "mdi-network-pos",
      visible: props.haveCreatePermission && canPayAtPos(),
    },
    {
      value: "coupon",
      label: "Coupon",
      icon: "mdi-ticket-percent-outline",
      visible: props?.type !== 'coupons' && props?.type !== "event"
    },
    {
      value: "giftCard",
      label: "Gift Card",
      icon: "mdi-wallet-giftcard",
      visible: props.type !== "giftCard" && props?.type !== "event",
    },
    {
      value: "creditCard",
      label: "Credit Card",
      icon: "mdi-credit-card-outline",
      visible:
        props.type !== "transferCredit" &&
        props?.type !== "event" &&
        currentLocationDetails.value?.onlineCreditCardsEnabled,
    },
    {
      value: "waiveFee",
      label: "Waive Fee",
      icon: "mdi-gift-open-outline",
      visible:
        props.haveCreatePermission &&
        ["event", "Booking"].includes(props?.type),
    },
    {
      value: "payInStore",
      label: "Pay In Store",
      icon: "mdi-store-marker",
      visible: canPayInStore(),
    },
    {
      value: "userCredit",
      label: "User Credit",
      icon: "mdi-account-credit-card-outline",
      visible:
        props.type !== "transferCredit" &&
        props?.type !== "event" &&
        (getuserCreditAmt.value.originalAmt > 0 || (currentLocationDetails.value?.allowUserNegAccountBalance && props?.userCreditCards?.length > 0))
        && props?.owner != pb?.authStore?.model?.id,
    },
  ];
  if(!props.haveCreatePermission){
     tabsList = [
      {
        value: "coupon",
        label: "Coupon",
        icon: "mdi-ticket-percent-outline",
        visible:props?.type !== 'coupons' && pb?.authStore?.model?.id && props?.type !== "event"
      },
      {
        value: "giftCard",
        label: "Gift Card",
        icon: "mdi-wallet-giftcard",
        visible: props.type !== "giftCard" && props?.type !== "event",
      },
      {
        value: "creditCard",
        label: "Credit Card",
        icon: "mdi-credit-card-outline",
        visible:
          props.type !== "transferCredit" &&
          props?.type !== "event" &&
          currentLocationDetails.value?.onlineCreditCardsEnabled,
      },
      {
        value: "payInStore",
        label: "Pay In Store",
        icon: "mdi-store-marker",
        visible: canPayInStore(),
      },
      {
        value: "userCredit",
        label: "User Credit",
        icon: "mdi-account-credit-card-outline",
        visible:
          props.type !== "transferCredit" &&
          props?.type !== "event" &&
          (getuserCreditAmt.value.originalAmt > 0 || (currentLocationDetails.value?.allowUserNegAccountBalance && props?.userCreditCards?.length > 0))
          && props?.owner === pb?.authStore?.model?.id,
      },
    ];
  }

  return tabsList.filter((tab) => tab.visible);
});


const canPayInStore = () => {
  return props.canPayInStore
}

const currentTab = ref<string>(tabs.value[0]?.value || "");

// Computed
/*
  This computed property calculates the surcharge amount based on the selected location's card surcharge fixed fee and percentage fee.
*/
const getSurchargeAmount = computed(() => {
  const selectedLocation = locationList.value?.find(
    (l: {
      id: string;
      cardSurchargeFixedFee?: number;
      cardSurchargePercentFee?: number;
    }) => l.id === currentLocation.value,
  );

  // Destructure with default values to ensure they're numbers
  const { cardSurchargeFixedFee = 0, cardSurchargePercentFee = 0 } =
    selectedLocation || {};

  let amount = props?.bookingAmount - parseFloat(props?.totalPaid || 0)
  // Calculate surcharge fee (fixed fee + percentage of the checkout total)
  const surchargeFee =
    cardSurchargeFixedFee +
    amount * (cardSurchargePercentFee / 100);

  // Return the final result formatted to two decimal places
  return surchargeFee.toFixed(2);
});

// method
const updateErrorMessage = (
  isVisible: boolean,
  message: string,
  color: any = null,
  icon: any = null,
) => {
  emit("updateMessage", {
    isVisible,
    message,
    color: color || "error",
    icon: icon || "tabler-xbox-x",
  });
};

/**
 * Validates the gift card entered by the user.
 *
 * @returns {Promise<void>}
 */
const validateGiftCard = async () => {
  try {
    // If no gift card ID is provided, return early
    if (!giftCardId.value) return;

    // Fetch the gift card details from the database
    const response = await pb.collection("giftCards").getOne(giftCardId.value);

    if (response) {
      // If the gift card is void, display an error message
      if (response?.void) {
        updateErrorMessage(true, "Gift Card is void");

        return;
      }

      // Store the gift card details
      appliedGiftCardDetails.value = response;

      // If the gift card balance is 0, display an error message
      if (response?.balanceRemaining <= 0) {
        isInvalidCoupon.value = true;
        updateErrorMessage(true, "Gift Card has 0 Balance");
      } else {
        // If the gift card balance is valid, update the amount to redeem
        isInvalidCoupon.value = false;

        // Calculate the minimum value between balanceRemaining and booking due amount
        amountToRedeem.value = Math.min(
          response.balanceRemaining,
          props?.totalDue || 0,
        );
        updateErrorMessage(false, "");
      }
    }
  } catch (e) {
    // If the gift card is not found, display an error message
    isInvalidCoupon.value = true;
    updateErrorMessage(true, "Gift Card Not Found");
  }
};

const createPendingPaymentRecord = async () => {
  const { valid } = (await refVFormCreditCard.value?.validate?.()) || {
    valid: true,
  };

  if (props?.loggedInUserId || (props?.owner == pb?.authStore?.model?.id)) {
    if (!valid) return;
  } else {
    selectedCreditCard.value = "newCard";
  }

  isProcessingCardDialogVisible.value = true;

  try {
    addCardDialogMessage.value = {
      isVisible: true,
      spinner: true,
      message: "",
      color: "",
    };

    const paymentData = {
      docket: docketId.value,
      amount: Round2String(props?.totalDue, 2),
      paymentType: "Credit Card",
      addCreditCard:
        saveNewCard.value && selectedCreditCard?.value === "newCard",
      billingEmail: pb?.authStore?.model?.email || "",
      billingPhone: pb?.authStore?.model?.mobileNumber || "",
      cardToken:
        selectedCreditCard?.value !== "newCard"
          ? selectedCreditCard?.value
          : "",
      user: props?.owner || "",
      status: "Pending",
      createdBy: userId.value,
      surchargeAmount: Number(getSurchargeAmount.value)?.toFixed(2),
      source: props?.source,
      webResponseLoc: window.location.origin,
    };

    // Create the payment record in the database
    const paymentRecord = await pb
      .collection("docketPayments")
      .create(paymentData);

    // update user into docket
    await pb.collection("dockets").update(docketId.value, {
      user: props?.owner,
      source: props?.source,
    });

    payingDocket.value = paymentRecord?.id;

    // Subscribe to the created payment record
    await subscribeBookingPayment(paymentRecord?.id);
  } catch (error) {
    console.error("Error creating pending payment record", error);
  }
};

const goToPos = async (docketId:any) => {

  let docketCheckID = docketId
  try{
    let docketLines = await pb.collection('docketLines').getFullList({filter: 'booking="' + props?.bookingId + '"', sort:"created", expand:"docket"})
    if(docketLines && docketLines.length > 0) {
      docketCheckID = docketLines[docketLines.length - 1]?.docket || docketCheckID
    }
  }catch(e){}

  emit("closeDialog", false);
  emit("goToPosPayment", docketCheckID);
}

const checkBookingBalance = async (type:string) => {
  await handleInitiateFlow(type)

  if(props?.bookingId) {
    let getBookingDetails = await pb.collection("bookings").getOne(props?.bookingId)
    if (getBookingDetails && getBookingDetails.state != 'Confirmed' && getBookingDetails.due <= 0) {
      await pb.collection("bookings").update(props?.bookingId, {state: 'Confirmed'});
      try {
        let PersontoEmail = getBookingDetails?.users && getBookingDetails?.users.length > 0 ? getBookingDetails.users[0] : getBookingDetails.owner
        let sendQue = {
          sendVia: 'email',
          type: 'Booking',
          status: 'New',
          recordID: props?.bookingId,
          user: PersontoEmail
        }
        await pb.collection("sendQueue").create(sendQue)
        emit('isConfirmed', true)
      } catch (e) {
      }
    }
  } else {
    emit('isConfirmed', true)
  }
}

const handleInitiateFlow = async (type: string) => {
  if(props?.bookingId){
    try{
      // check if Booking is Confirmed
      let getBookingDetails = await pb.collection("bookings").getOne(props?.bookingId)
      if(getBookingDetails && getBookingDetails.state != 'Confirmed') {
        if(type == "CreditCard" || type == "GiftCard" || type == "pos" || type == "Coupon"){
          await pb.collection("bookings").update(props?.bookingId, {state: 'Processing'});
        } else {
          await pb.collection("bookings").update(props?.bookingId, {state: 'Confirmed'});
          let PersontoEmail = getBookingDetails?.users && getBookingDetails?.users.length > 0 ? getBookingDetails.users[0] : getBookingDetails.owner
          try {
            let sendQue = {
              sendVia: 'email',
              type: 'Booking',
              status: 'New',
              recordID: props?.bookingId,
              user: PersontoEmail
            }
            await pb.collection("sendQueue").create(sendQue)
            emit('isConfirmed',true)
          } catch (e) {}
        }
      }
    } catch(e){
      alert("Error With Booking - Please Start the Booking for a Bay Again")
      return
    }
  }

  try {
    updateErrorMessage(
      true,
      "Payment Inprogress...",
      "warning",
      "mdi-account-credit-card",
    );

    // Validate form based on type
    if (["Coupon", "GiftCard", "WaiveFee", "UserCredit"].includes(type)) {
      const form = {
        Coupon: refVFormCoupon,
        GiftCard: refVFormGiftCard,
        WaiveFee: refVFormWaive,
        UserCredit: refCreditAmtForm,
      }[type]?.value;

      const { valid } = (await form?.validate?.()) || { valid: true };

      if (!valid) return;
    }

    if (props?.type === "Booking") {
      let amount = props?.bookingAmount || 0
      let qty = props?.bookingSlot?.slotQty || 1
      let sessionPrice = props?.bookingSlot?.sessionPrice || 0;
      if(props.bookingAmount && props?.bookingSlot?.sessionPrice){
        qty = Math.floor(props.bookingAmount / props?.bookingSlot?.sessionPrice)
      }

      if(props?.bookingId && props?.bookingAmount){
        try{
          let docketLineCheck = await pb.collection('bookings').getOne(props?.bookingId, {expand:'activity'})
          if(docketLineCheck && docketLineCheck.sessionFullPrice){
            qty = Math.floor(props.bookingAmount / docketLineCheck.sessionFullPrice)
            sessionPrice = docketLineCheck.sessionFullPrice
          }
        }catch(e){}

        // Has there been a past booking
        // Is this a booking for minutes
        let doingMinuteSales = false
        try{
          let docketLines = await pb.collection('docketLines').getFullList({filter: 'booking="' + props?.bookingId + '"', sort:"created"})
          if(docketLines && docketLines.length > 0) {
            docketLines.forEach((dl: any, dli: number) => {
              if (dli < docketLines.length - 1 && dl.itemPrice > 0 && dl.itemPrice >= sessionPrice) {
                qty = qty - dl.qty
                amount = sessionPrice * qty
              } else if (dl.itemPrice > 0 && dl.itemPrice < sessionPrice){ // Actual Minutes Being Sold
                doingMinuteSales = true
                amount += dl.itemPrice * dl.qty
                qty += dl.qty
              }
            })
            docketLineId.value = docketLines[docketLines.length - 1].id
            docketId.value = docketLines[docketLines.length - 1].docket // UPDATE DOCKET
            if(doingMinuteSales){
              sessionPrice = amount;
            }
          }
        }catch(e){}
      }

      if(qty < 1){
        qty = 1
      }

      const bookingDetails = {
        location: props?.location,
        discountProduct: props?.discountProduct,
        discountBookings: props?.discountBookings,
        discountEvents: props?.discountEvents,
        discountSources: props?.discountSources,
        discCodeBookings: props?.discCodeBookings,
        discCodeProduct: props?.discCodeProduct,
        discMemBookings: props?.discMemBookings,
        discMemProducts: props?.discMemProducts,
        due: props?.totalDue,
        loginUser: props?.loggedInUserId || props?.owner,
        amount: amount,
        owner: props?.owner,
        paid: props?.totalPaid,
        qty: qty,
        itemPrice: sessionPrice,
        payMethods: props?.payMethods,
      };

      await initiateDocketForBooking(
        bookingDetails,
        props?.bookingId,
        props?.source,
        props?.products,
      );

      try {
        // Handle different payment types
        switch (type) {
          case "Coupon":
          case "GiftCard":
            await handleApplyProcess(type);
            if (!props?.loggedInUserId) {
              emit("bookingChange", {
                bookingId: props?.bookingId,
                payLater: true,
              });
            }
            break;
          case "pos":
            emit("closeDialog", false);
            emit("goToPosPayment", docketId.value);
            break;
          case "CreditCard":
            await createPendingPaymentRecord();
            break;
          case "UserCredit":
          case "WaiveFee":
            const paymentData = {
              ...bookingDetails,
              creditAmt: creditAmtTopay.value,
            };

            await handleUserCreditAndWaiveFeePayment(
              paymentData,
              props?.bookingId,
              type,
              feeWaiveReason.value,
              props?.source,
            );

            emit("paymentSuccess", true);
            break;
        }
      } catch (err: any) {
        if (err?.message)
          updateErrorMessage(true, err?.message, "error", "tabler-xbox-x");
      }

      return;
    } else if (props?.type === "event") {
      const details = {
        eventId: props?.eventId,
        loggedInUser: props?.loggedInUserId,
        activityMap: props?.activityMap,
        due: props?.totalDue,
        location: props?.location,
        owner: props?.owner,
      } as any;

      switch (type) {
        case "pos":
          await eventDuePayByPos(details);
          emit("goToPosPayment", docketId.value);
          return;
        case "WaiveFee":
          await eventDuePayByWaiveFee(details);
          emit("paymentSuccess", true);
          break;
      }
    } else {
      // coupon/giftCard/userCredit
      const paymentDetails = {
        ...props?.requestedPaymentDetails,
        owner: props?.owner,
        loggedInUserId: props?.loggedInUserId,
        location: props?.location,
        identifier: giftCardId.value,
        emailAddress: props?.emailAddress,
      };

      await createEditDocketForCustomizePurchase(paymentDetails, props?.type);

      // Handle different payment types
      try {
        switch (type) {
          case "GiftCard":
            paymentDetails.amount = amountToRedeem.value;
            await payByGiftCard(paymentDetails, props?.type, props?.source);
            break;
          case "UserCredit":
            await payByUserCredit(paymentDetails, props?.type, props?.source);
            break;
          case "pos":
            await payByPos(paymentDetails, props?.type);
            emit("goToPosPayment", docketId.value);
            return;
        }
        toast.success("Payment successful", {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        docketId.value = "";
        docketLineId.value = "";
        emit("paymentApproved", false);
        emit("closeDialog", false);
      } catch (e) {
        if (!lowBalanceError?.value) {
          toast.error("Something went wrong.", {
            position: toast.POSITION.BOTTOM_RIGHT,
          });
          docketId.value = "";
          docketLineId.value = "";
          emit("closeDialog", false);
        }
      }
    }
  } catch (e) {
    console.error(e);
  }
};

const couponUser = ref(props?.bookingProp?.users[0] || props?.owner || '')
/**
 * Function to calculate and set the required quantity for a coupon to cover the total due.
 * UPDATED 1/04/2025 to remove Products
 * @function handleRequiredQuantity
 * @returns {void}
 */
const handleRequiredQuantity = async () => {
  let maxCoupons = 100;

  if(props?.couponCost){
    maxCoupons = props?.couponCost
  } else if(props?.bookingProp?.activity) {
    try {
      let maxCouponDB = await pb.collection('activities').getOne(props.bookingProp.activity)
      if(maxCouponDB && maxCouponDB?.couponCost && maxCouponDB?.couponCost > 0){
        maxCoupons = Math.floor(maxCouponDB.couponCost)
      }
    } catch(e){}
  }

  // Coupon value, default to 1 if not provided
  const couponValue = props?.couponValue ?? 1;

  // Booking discount, default to 0 if not provided
  const discount = props?.bookingDiscount ?? 0;

  // Total due, default to 0 if not provided
  let totalDue = props?.totalDue ?? 0;

  // Calculate the maximum quantity that can be applied to cover the total due

  if(props?.products && props.products.length > 0){
    props.products.forEach(product => {
      totalDue = totalDue - product.itemPrice;
    })
  }

  let maxRequiredQuantity = Math.ceil(
    (totalDue * 100) / (couponValue * (100 - discount)),
  );

  // If we need more coupons than allowed...
  if(maxRequiredQuantity > maxCoupons){
    maxRequiredQuantity = Math.floor(maxCoupons)
  }

  // Find the selected coupon from the list of coupons
  const couponSelected = getCoupons.value?.find(
    (el: any) => el.id === selectedCoupon.value,
  );

  // Determine the quantity to use: the lesser of maxRequiredQuantity or qtyRemaining
  quantity.value = Math.min(
    maxRequiredQuantity,
    couponSelected?.qtyRemaining || 0,
  );
};

const usedCoupons:Ref<any[]> = ref([])
const getDocketsInUse = async () => {
  usedCoupons.value = [];
  try {
    if (props.bookingId) {
      // Get DocketLine With That Booking ID
      let getDocketLine = await pb.collection("docketLines").getFirstListItem('booking="' + props.bookingId + '"')
      if (getDocketLine && getDocketLine.docket) {
        let docketList = await pb.collection("docketPayments").getFullList({filter: 'paymentType ="Coupon" && docket="' + getDocketLine.docket + '"'});
        docketList.forEach(coup => {
          usedCoupons.value.push({id: coup.docket, coupon: coup.coupon})
        })
      }
    }
  } catch (e) {
  }
}

const getListCoupons = () => {
  let couponsClean:any = [];
  getCoupons.value.forEach(c => {
    let cCopy = JSON.parse(JSON.stringify(c))
    usedCoupons.value.forEach(coup => {
      if (coup.coupon == cCopy.id) {
        cCopy.qtyRemaining = Math.floor(cCopy.qtyRemaining)
        if(cCopy.qtyRemaining && cCopy.qtyRemaining > 0) {
          //cCopy.qtyRemaining  // Remove if used in this booking
        }
      }
    })
    couponsClean.push(cCopy)
  })
  return couponsClean.filter((c:any) => c.qtyRemaining > 0)
}
const getCouponsToUseCount = () => {
  let couponCount = 0
  getListCoupons().forEach(coupon => {
    couponCount += coupon.qtyRemaining
  })
  return couponCount
}

const MaxCouponsAllowed = ref(1)
const MaxCouponsToUse = async () => {
  if(props?.bookingProp?.activity) {
    try {
      let maxCouponDB = await pb.collection('activities').getOne(props.bookingProp.activity)
      if(maxCouponDB && maxCouponDB?.couponCost && maxCouponDB?.couponCost > 0){
        MaxCouponsAllowed.value = Math.floor(maxCouponDB.couponCost)
        if(props?.couponCost){
          MaxCouponsAllowed.value = props.couponCost
        }
      }

      if(props?.bookingSlot?.slotQty) {
        MaxCouponsAllowed.value = MaxCouponsAllowed.value * props.bookingSlot.slotQty
      } else if(props?.bookingProp?.duration && maxCouponDB?.sessionDuration) {
        MaxCouponsAllowed.value = MaxCouponsAllowed.value * Math.floor((props.bookingProp.duration / maxCouponDB.sessionDuration))
      }
      if(props?.bookingAmount && props?.totalPaid) {
        let couponCost = maxCouponDB?.couponCost ? maxCouponDB?.couponCost : props?.couponValue ? props?.couponValue : 1
        if(props?.couponCost){
          couponCost = props?.couponCost
        }
        const couponValue = maxCouponDB?.sessionFullPrice ? maxCouponDB?.sessionFullPrice / couponCost : couponCost;   // 30
        const discount = props?.discountBookings ? props?.discountBookings : props?.bookingDiscount ? props?.bookingDiscount : 0;
        let couponValueAfterDiscount = Number(
          (couponValue * 1 * (100 - discount)) / 100,
        ).toFixed(2);
        let newAmount = parseFloat(props.bookingAmount) * (100 - discount) / 100 // Applies to Booking Only
        if(props?.bookingProp?.due && ((props.bookingProp.due % couponValueAfterDiscount) == 0)){
          // Booking is Perfect Match to Coupon
          MaxCouponsAllowed.value = Math.floor(props?.bookingProp?.due / couponValueAfterDiscount)
        } else {
          newAmount = newAmount - parseFloat(props.totalPaid)
          if (props?.totalDue == couponValueAfterDiscount) {
            newAmount = couponValueAfterDiscount
          }
          if (newAmount > 0 && couponValueAfterDiscount > 0) {
            MaxCouponsAllowed.value = Math.floor(newAmount / couponValueAfterDiscount)
          }
        }
      }
      } catch(e){}
  }
}

const getCouponsToUseCountArray = () => {
  let items = [];
  for(let j=0;j<getCouponsToUseCount();j++){
    if(items.length < MaxCouponsAllowed.value) {
      items.push(j+1)
    }
  }
  return items
}

const doNewApplyCoupons = async () => {
  await newApplyCoupons()
  // Prefill max coupons select field
  CouponsToApply.value = getCouponsToUseCountArray().length
  await totalDueIsNotProducts()
}

const doingCouponProcess = ref(false)
const newApplyCoupons = async() => {
  // Get oldest coupon
  if(CouponsToApply.value && CouponsToApply.value > 0) {
    doingCouponProcess.value = true
    let couponList = getListCoupons()
    let coupon = couponList.sort((a: any, b: any) => {
      let aDate = new Date(a.expiryTime).getTime()
      let bDate = new Date(b.expiryTime).getTime()
      return aDate == bDate ? 0 : aDate < bDate ? 1 : -1
    }).pop()

    if (coupon.qtyRemaining >= CouponsToApply.value) {
      selectedCoupon.value = coupon.id
      quantity.value = CouponsToApply.value
      await checkBookingBalance('Coupon')
    } else {
      if (parseInt(coupon.qtyRemaining) > 0) {
        selectedCoupon.value = coupon.id
        quantity.value = parseInt(coupon.qtyRemaining) //((use it up))
        await checkBookingBalance('Coupon')
        CouponsToApply.value = parseInt(CouponsToApply.value) - parseInt(coupon.qtyRemaining)
        await newApplyCoupons()
      }
    }
    await MaxCouponsToUse()
    doingCouponProcess.value = false
  }
}

/**
 * Handles the payment process for either a coupon or a gift card.
 *
 * @param {string} type - The type of payment to be processed. It can be either 'Coupon' or 'Gift Card'.
 *
 * @returns {Promise<void>} - A promise that resolves when the payment process is completed successfully.
 *
 * @throws {Error} - Throws an error if there is an issue with the payment process.
 */
const handleApplyProcess = async (type: string) => {
  try {
    let amount;
    let paymentType;
    let identifier;

    if (type === "Coupon") {
      // COUPON CAN ONLY PAY FOR BOOKING - NOT PRODUCTS (EXTRAS)
      // If Not props.couponValue = work it out again
      let sessionPrice = 0
      if(props?.bookingProp?.activity){
        try {
          let bookingValueCheck = await pb.collection("activities").getOne(props?.bookingProp?.activity)
          if(bookingValueCheck && bookingValueCheck.sessionFullPrice){
            sessionPrice = bookingValueCheck.sessionFullPrice / bookingValueCheck.couponCost
            if(props?.couponCost){
              sessionPrice = bookingValueCheck.sessionFullPrice / props.couponCost
            }
          }
        } catch(e){}
      }
      const couponValue = sessionPrice ? sessionPrice : props?.couponValue ?? 1;   // 30
      const discount = props?.discountBookings ? props?.discountBookings : props?.bookingDiscount ? props?.bookingDiscount : 0;
      amount = Number(
        (couponValue * quantity.value * (100 - discount)) / 100,
      ).toFixed(2);
      paymentType = "Coupon";
      identifier = selectedCoupon.value;
    } else {
      amount = Number(amountToRedeem.value).toFixed(2);
      paymentType = "Gift Card";
      identifier = giftCardId.value;
    }

    const params = {
      status: "Approved",
      paymentType,
      amount,
      identifier,
      qty: quantity.value,
      toPay: props?.totalDue,
      type: props?.type,
      bookingId: props?.bookingId,
      totalPaid: props?.totalPaid,
      userId: props?.loggedInUserId || props?.owner,
      payMethods: props?.payMethods,
      source: props?.source,
      giftValue: appliedGiftCardDetails.value,
    };

    try {

      const response = await payWithGiftCardOrCoupon(params);

      if (response) {
        if(type === "Coupon"){
          // update Coupon deduct it by quantity
          let couponBalance = await pb.collection('coupons').getOne(selectedCoupon.value)
          if(couponBalance){
            let balance = Math.floor(couponBalance.qtyRemaining) - quantity.value
            await pb.collection('coupons').update(selectedCoupon.value, {qtyRemaining:balance})
          }
        }

        clearForm();
        emit("paymentSuccess", true);
        await getCouponList();
        await getDocketsInUse()
      }
    } catch (e) {}
  } catch (e) {
    console.error(e);
  }
};

const handleDiscountCode = async (code: string) => {
  try {
    updateErrorMessage(true, "Processing your request...", "warning", "");

    await confirmBooking();

    // Determine booking or event type details and prepare data accordingly
    const isBooking = props?.type === "Booking";

    // Check if docket already exists
    if (!docketId.value) {
      const details = isBooking
        ? {
            location: props?.location,
            discountProduct: props?.discountProduct,
            discountBookings: props?.discountBookings,
            discountEvents: props?.discountEvents,
            discCodeBookings: props?.discCodeBookings,
            discCodeProduct: props?.discCodeProduct,
            discMemBookings: props?.discMemBookings,
            discMemProducts: props?.discMemProducts,
            due: props?.totalDue,
            loginUser: props?.loggedInUserId,
            amount: props?.bookingAmount,
            owner: props?.owner,
            paid: props?.totalPaid,
            qty: props?.bookingSlot?.slotQty,
            itemPrice: props?.bookingSlot?.sessionPrice,
            payMethods: props?.payMethods,
          }
        : {
            eventId: props?.eventId,
            discountProduct: props?.discountProduct,
            discountBookings: props?.discountBookings,
            discountEvents: props?.discountEvents,
            discCodeBookings: props?.discCodeBookings,
            discCodeProduct: props?.discCodeProduct,
            discMemBookings: props?.discMemBookings,
            discMemProducts: props?.discMemProducts,
            loggedInUser: props?.loggedInUserId,
            activityMap: props?.activityMap,
            due: props?.totalDue,
            location: props?.location,
            owner: props?.owner,
          };

      // Initiate docket if not present
      if (isBooking) {
        await initiateDocketForBooking(
          details,
          props?.bookingId,
          props?.source,
          props?.products,
        );
      } else {
        await initiateDocketForEvent(details);
      }
    }

    // Check if the discount Code is Valid for the booking Date
    let canApply = await validateBookingDateAppliedDiscountCode(props?.bookingProp);

    if(canApply) {
      // Apply discount code after ensuring docket exists
      await handleDiscountCodeApply(
        isBooking ? "BOOKING" : "EVENT",
        docketId.value,
      );
    }

    emit("refetch", true);
  } catch (e) {
    console.error("Error applying discount code:", e);
  }
};

/**
 * Handles the error scenario when the user's credit balance is insufficient.
 * It checks if the user has any credit cards and displays appropriate error messages.
 * If no cards exist, it prompts the user to add a new card.
 *
 * @returns {Promise<void>} - A promise that resolves when the error handling process is completed.
 *
 * @throws {Error} - Throws an error if there is an issue with checking the user's cards.
 */
const handleCreditError = async () => {
  try {
    // Check if any card exists
    const resultList = await pb.collection("bankCards").getList(1, 50, {
      filter: `user = "${props?.owner}"`,
    });

    const cards = resultList?.items || [];
    const primaryCard = cards.find((card: any) => card?.isPrimary);

    if (primaryCard) {
      // Extract the last 4 digits of the card number
      const lastFourDigits = primaryCard.cardNum.slice(-4);

      updateErrorMessage(
        true,
        `Insufficient balance. The remaining amount will be charged to your card ending in ${lastFourDigits}.`,
        "error",
        null,
      );
    } else if (cards.length) {
      // If there are cards but no primary card, notify the user
      updateErrorMessage(
        true,
        "Insufficient balance. No primary card found. Please add a primary card.",
        "error",
        null,
      );
    } else {
      // If no cards exist, prompt the user to add a new card
      updateErrorMessage(
        true,
        "Insufficient balance and no credit card on file. Please add a new card into your account.",
        "error",
        null,
      );
      setTimeout(() => {
        emit("addNewCardDialog", true);
      }, 2000);
    }
  } catch (e) {
    updateErrorMessage(
      true,
      "An error occurred while checking your cards. Please try again later.",
      "error",
      null,
    );
    if (props?.loggedInUserId) {
      emit("closeDialog", false);
    }
  } finally {
    setTimeout(() => {
      clearForm();
      if (props?.loggedInUserId) {
        emit("closeDialog", false);
      }
    }, 3000);
  }
};

// This function is used to close the processing card dialog.
const closeProcessingCardDialog = async () => {
  try {
    // Fetch the current record for the docket payment
    const currRecord = await pb
      .collection("docketPayments")
      .getOne(`${payingDocket.value}`);

    // Check if the payment is in "Pending" status
    if (currRecord?.status === "Pending") {
      const updatedRecord = await pb
        .collection("docketPayments")
        .update(`${payingDocket.value}`, {
          status: "Canceled",
        });

      // Update dialog message if the record was successfully updated
      if (updatedRecord) {
        addCardDialogMessage.value = {
          isVisible: true,
          color: "error",
          message: `Payment ${updatedRecord.status}.`,
          spinner: false,
        };
        isProcessingCardDialogVisible.value = false;
        return;
      }
    }
  } catch (error) {
    console.error("Error cancelling docket payment:", error);
  } finally {
    // Ensure dialog visibility and message are reset in all cases
    isProcessingCardDialogVisible.value = false;
    addCardDialogMessage.value = {
      isVisible: false,
      color: "",
      message: "",
      spinner: false,
    };
  }
};

const clearForm = () => {
  selectedUser.value = null;
  selectedCoupon.value = null;
  quantity.value = null;
  giftCardId.value = "";
  appliedGiftCardDetails.value = "";
  amountToRedeem.value = undefined;
  isInvalidCoupon.value = false;
  lowBalanceError.value = false;
};

// watcher
watch(
  () => [props?.owner, lowBalanceError?.value],
  async ([newOwner, newLowBalanceError]) => {
    // Handle owner changes
    await getCouponList();

    creditAmtTopay.value = props?.totalDue || creditAmtTopay.value
    // Handle lowBalanceError changes
    if (newLowBalanceError) await handleCreditError();
  },
  { immediate: true },
);

/**
 * Check Product Costs - Remove these From Coupons
 */

const totalDueNotProducts = ref(true)
const totalDueIsNotProducts = async() => {
  let sessionPrice = 0

  if(props?.bookingProp?.activity){
    try {
      let bookingValueCheck = await pb.collection("activities").getOne(props?.bookingProp?.activity)
      if(bookingValueCheck && bookingValueCheck.sessionFullPrice){
        sessionPrice = bookingValueCheck.sessionFullPrice
      }
    } catch(e){}
  }
  const couponValue = sessionPrice ? sessionPrice : props?.couponValue ?? 30

  const discount = props?.discountBookings ? props?.discountBookings : props?.bookingDiscount ? props?.bookingDiscount : 0;

  let couponValueAfterDiscount = parseFloat((parseFloat(couponValue) * 1 * (100 - discount)) / 100);

  if(props?.bookingAmount) {
    let newAmount = parseFloat(props.bookingAmount) * (100 - discount) / 100 // Applies to Booking Only
    if (props?.totalPaid) {
      newAmount = newAmount - parseFloat(props.totalPaid)
    } else if(props?.bookingProp?.paid){
      newAmount = newAmount - parseFloat(props.bookingProp.paid)
    }

    if(props?.totalDue == couponValueAfterDiscount || (props?.totalDue * 2 == couponValueAfterDiscount)){
      newAmount = couponValueAfterDiscount
    }

    if (newAmount > 0 && couponValueAfterDiscount > 0) {
      let valueCheck = Math.floor(newAmount / couponValueAfterDiscount)
      if (valueCheck < 1) {
        totalDueNotProducts.value = false
      }
    } else {
      totalDueNotProducts.value = false
    }
  } else {
    totalDueNotProducts.value = true
  }
}

const onUpdateMenu = (open:boolean) =>{
  if(open){
    setTimeout(() => window.dispatchEvent(new Event("resize")), 50);
  }
}

const CouponsToApply = ref()

const confirmBooking = async () => {
  try {
    let getBookingDetails = await pb.collection("bookings").getOne(props?.bookingId)
    if(getBookingDetails && getBookingDetails.state != 'Confirmed') {
      await pb.collection("bookings").update(props?.bookingId, {state: 'Confirmed'});
      try {
        let PersontoEmail = getBookingDetails?.users && getBookingDetails?.users.length > 0 ? getBookingDetails.users[0] : getBookingDetails.owner
        let sendQue = {
          sendVia: 'email',
          type: 'Booking',
          status: 'New',
          recordID: props?.bookingId,
          user: PersontoEmail
        }
        await pb.collection("sendQueue").create(sendQue)
      } catch (e) {
      }
    }
    emit('isConfirmed', true)
  } catch(e){}
}

const goToProfile = () => {
  showPreventBenefits.value = false
  emit('closeDialog', false);
  router.push('/user/' + props?.owner)
}

const eventDocketID = ref('')
const getEventDocket = async () => {
  eventDocketID.value = ''
  if(props?.type == "event" && props?.eventId){
    // Get All Bookings
    try {
      let allBookings = await pb.collection("events").getOne(props?.eventId, {fields:'bookings'})
      // Get All DocketLines for Bookings
      let allBookingsQuery = allBookings?.bookings?.map((booking:string) => 'booking="' + booking + '"').join('||')

      let allBookingsCheck = await pb.collection("docketLines").getFullList(
        {
          filter: 'transactionType="Event" && (' + allBookingsQuery + ')',
          expand: 'docket',
          fields: 'docket,expand.docket.status'
        })

      if (allBookingsCheck) {
        allBookingsCheck.forEach(booking => {
          if (booking?.expand?.docket?.status != "Paid" && booking?.expand?.docket?.status != "Closed" && eventDocketID.value == '') {
            eventDocketID.value = booking?.docket || ''
            docketId.value = eventDocketID.value
          }
        })
      }
    } catch(e){
      try {
        let eventCheck = await pb.collection("events").getOne(props?.eventId, {expand: 'bookings,bookings.docketLine'})
        eventDocketID.value = eventCheck?.expand?.bookings?.length > 0 && eventCheck?.expand?.bookings[0]?.expand?.docketLine?.docket ? eventCheck?.expand?.bookings[0]?.expand?.docketLine?.docket : ''
      } catch(e){}
    }
  }
}

onMounted(async ()=>{
  usersForCouponsList.value = [];
  localStorage.setItem('onpayment','1')
  if(props?.userCreditCards && props.userCreditCards.length > 0){
    selectedCreditCard.value = props.userCreditCards[0].token
  }
  await getDocketsInUse()
  await MaxCouponsToUse()
  doBarcodeReaderSubscribe()

  creditAmtTopay.value = props.totalDue

  currentTab.value = props.haveCreatePermission && canPayAtPos() ? 'pos' : pb?.authStore?.model?.id ? currentTab.value : "creditCard"

  if(props?.owner) {
    try {
      let hasMembershipBenefits = await pb.collection("users").getOne(props.owner);
      if (hasMembershipBenefits && hasMembershipBenefits.preventBenefits) {
        showPreventBenefits.value = true
      }
    } catch(e){}
  }

  if(props?.bookingProp?.users?.length > 0){
    let users:any = [];
    let filter = 'id="' + (pb?.authStore?.model?.id || '') + '"'
    couponUser.value = pb?.authStore?.model?.id || ''
    if(props?.haveCreatePermission){
      users = users.concat(props?.bookingProp?.users || [], [props.owner])
      filter = users.map((u:any) => 'id="'+ u +'"').join("||")
      couponUser.value = props?.bookingProp?.users && props.bookingProp.users[0] ? props.bookingProp.users[0] :  props.owner
    }
    let fields = "id,firstName,lastName,username"
    usersForCouponsList.value = await pb.collection("users").getFullList({filter,fields});
  } else if(pb?.authStore?.model?.id) {
    let fields = "id,firstName,lastName,username"
    let filter = 'id="' + (pb?.authStore?.model?.id || '') + '"'
    usersForCouponsList.value = await pb.collection("users").getFullList({filter,fields});
  }

  // Prefill max coupons select field
  CouponsToApply.value = getCouponsToUseCountArray().length
  await totalDueIsNotProducts()
  await getEventDocket()
})
</script>

<template>
  <div>
    <VRow>
      <VCol sd cols="12" sm="4" md="3" class="position-relative pt-5">
        <!-- 👉 Tabs -->
        <VTabs
          v-model="currentTab"
          direction="vertical"
          class="v-tabs-pill"
          grow
        >
          <VTab v-for="item in tabs" :key="item.icon" :value="item.value">
            <VIcon start :icon="item.icon" />
            {{ item.label }}
          </VTab>
        </VTabs>
        <!-- Add vertical divider inside the first column -->
        <VDivider vertical class="divider-absolute" />
      </VCol>

      <VCol cols="12" sm="8" md="9">
        <!-- 👉 Windows -->
        <VWindow v-model="currentTab" :touch-less="true">
          <div class="info-button-container">
            <VTooltip
              v-model="isTooltipVisible"
              activator="click"
              location="top"
              content-class="payment_info_tooltip"
            >
              <template #activator="{ props }">
                <IconBtn
                  v-bind="props"
                  icon="mdi-information-outline"
                  class="info-icon"
                  @click.stop
                  @touchstart.stop="isTooltipVisible = !isTooltipVisible"
                />
              </template>
              <div class="tooltip-content">
                <span v-if="currentTab === 'coupon'">
                      <strong>Apply Coupon:</strong><br>Select a coupon from the
                      dropdown list and apply it. The required quantity will be
                      deducted from the selected coupon.
                </span>
                <span v-else-if="currentTab === 'userCredit'">
                  The total amount will be deducted from your available account
                  balance.
                </span>
                <span v-else-if="currentTab === 'creditCard'">
                  Enter your credit card details to pay directly with your credit
                  card securely online .
                </span>
                <span v-else-if="currentTab === 'payInStore'">
                  Select this option to pay on arrival at our venue.
                </span>
                <span v-else-if="currentTab === 'pos'">
                  You will be redirected to the Point of Sale (POS) screen to
                  complete the payment. Please ensure all details are correct
                  before proceeding.
                </span>
                <span v-else-if="currentTab === 'giftCard'">
                  Enter the gift card code in the provided field. We will
                  validate the code and apply the corresponding balance to your
                  purchase.
                </span>
                <span v-else-if="currentTab === 'waiveFee'">
                  Please ensure you have a valid reason to Waive the fee!.
                </span>
              </div>
            </VTooltip>
          </div>

          <!-- Coupons  -->
          <VWindowItem
            v-if="props?.type !== 'coupons'"
            value="coupon"
          >
            <VCardText class="pt-0 pb-0">
              <VCardTitle class="d-flex justify-left px-0 pt-0">
                Pay with coupons
              </VCardTitle>
              <div v-if="getUsersForCouponsList().length > 1" class="mb-5">
                <VSelect
                  label="Select a player"
                  placeholder="Select a player"
                  clearable
                  :rules="[!couponUser || couponUser == 0 ? 'Select a player ': true]"
                  v-model="couponUser"
                  :items="getUsersForCouponsList()">
                </VSelect>
              </div>
              <div v-if="getCoupons.length > 0">
              <VForm ref="refVFormCoupon" v-if="totalDueNotProducts">
                <VRow v-if="!doingCouponProcess">
                  <VCol cols="12" class="d-flex">
                    <VSelect
                      label="Select Number of Coupons to Use"
                      placeholder="Select Number of Coupons to Use"
                      clearable
                      :rules="[!CouponsToApply || CouponsToApply == 0 ? 'Please Select Number of Coupons to use': true]"
                      v-model="CouponsToApply"
                      @update:modelValue="handleRequiredQuantity"
                      :items="getCouponsToUseCountArray()">
                    </VSelect>
                  </VCol>
                </VRow>
                <VCardActions v-if="!doingCouponProcess" class="d-flex justify-end mt-2">
                  <VRow class="d-flex justify-end">
                    <VCol
                      class="d-flex flex-column flex-xs-column flex-sm-row justify-end gap-2 px-0 mt-5">
                      <VBtn
                        variant="flat"
                        color="primary"
                        :disabled="!CouponsToApply || CouponsToApply < 1"
                        @click.prevent="doNewApplyCoupons()">
                        Apply Coupon<span v-if="CouponsToApply > 1">s</span>
                      </VBtn>
                    </VCol>
                  </VRow>
                </VCardActions>
                <div v-else>
                  <v-progress-circular indeterminate></v-progress-circular> Processing...
                </div>
              </VForm>
              <div v-else>
                <VAlert color="success mb-3" v-if="props?.bookingProp?.payMethod && props.bookingProp.payMethod.includes('Coupon')">
                  <h3 class="text-white">Coupons have been applied to the booking.</h3></VAlert>
                <VAlert color="error" v-if="totalDueNotProducts">Coupons can only be used for Bookings.</VAlert>
              </div>
              </div>
              <div v-else>
                <VAlert color="">Account has no coupons for this booking date available</VAlert>
              </div>
            </VCardText>
          </VWindowItem>

          <!-- user Credit -->
          <VWindowItem
            v-if="props?.type !== 'transferCredit'"
            value="userCredit"
          >
            <VCardText class="pt-0 pb-0">
              <VCardTitle class="d-flex justify-left px-0 pt-0">
                Pay By Account Credit
              </VCardTitle>
              <div class="mb-4">
                Make the Payment from your account balance.
              </div>
              <VForm ref="refCreditAmtForm">
                <VRow>
                  <VCol cols="6">
                    <VTextField
                      v-model="creditAmtTopay"
                      :rules="[
                        requiredValidator,
                        creditLimitValidator(
                          creditAmtTopay,
                          currentLocationDetails?.allowUserNegAccountBalance ? props.totalDue : getuserCreditAmt.originalAmt,
                          props.totalDue,
                        ),
                      ]"
                      label="Enter Amount"
                      placeholder="60"
                      type="number"
                      min="1"
                      :max="props.totalDue"
                    />
                  </VCol>
                </VRow>
              </VForm>
              <VCardActions class="d-flex justify-end mt-5 px-0">
                <VBtn
                  variant="flat"
                  color="primary"
                  class="mt-16"
                  @click.prevent="checkBookingBalance('UserCredit')"
                >
                  Pay
                </VBtn>
              </VCardActions>
            </VCardText>
          </VWindowItem>

          <!-- pos payment -->
          <VWindowItem v-if="props?.haveCreatePermission" value="pos">
            <VCardText class="pt-0 pb-0">
              <VCardTitle class="d-flex justify-left px-0 pt-0">
                Pay from POS screen
              </VCardTitle>
              <div>
                This payment method allows you to use the POS to pay for the
                booking.
              </div>
              <VCardActions class="d-block" style="text-align:right">
                <VBtn v-if="props?.bookingProp?.docketId" @click.prevent="goToPos(props?.bookingProp?.docketId)"
                      width="200"
                      prepend-icon="mdi-network-pos"
                      variant="flat"
                      color="primary"
                      class="mt-16">
                  Pay from pos
                </VBtn>
                <VBtn v-else-if="props?.bookingProp?.expand?.docketLine?.docket"
                      width="200"
                      prepend-icon="mdi-network-pos"
                      variant="flat"
                      color="primary"
                      class="mt-16"
                      @click.prevent="goToPos(props?.bookingProp?.expand?.docketLine?.docket)">
                  Pay from pos
                </VBtn>
                <VBtn v-else-if="eventDocketID==''"
                      width="200"
                      prepend-icon="mdi-network-pos"
                      variant="flat"
                      color="primary"
                      class="mt-16"
                      @click.prevent="checkBookingBalance('pos')">
                  Pay from pos
                </VBtn>
                <VBtn v-else-if="eventDocketID!==''"
                      width="200"
                      prepend-icon="mdi-network-pos"
                      variant="flat"
                      color="primary"
                      class="mt-16"
                      @click.prevent="goToPos(eventDocketID)">
                  Pay from pos
                </VBtn>
                <div></div>
                <VBtn v-if="!props?.bookingProp?.docketLine && eventDocketID == ''"
                      width="200"
                      prepend-icon="mdi-cart"
                      variant="flat"
                      color="primary"
                      class="mt-3"
                      @click.prevent="addToDocket(props)">
                  Add To Docket
                </VBtn>

              </VCardActions>
            </VCardText>
          </VWindowItem>

          <!-- gift card payment -->
          <VWindowItem v-if="props?.type !== 'giftCard'" value="giftCard">
            <VCardText class="pt-0 pb-0">
              <VCardTitle class="d-flex justify-left px-0 pt-0">
                Pay from Gift Card
              </VCardTitle>
              <div class="mb-2">
                Use all or part of a Gift Card to pay for this booking.
              </div>

              <!-- gift card form -->
              <VForm ref="refVFormGiftCard">
                <VRow>
                  <!-- gift card balance -->
                  <VCol
                    v-if="!isInvalidCoupon && !isEmpty(appliedGiftCardDetails)"
                    class="d-flex justify-center"
                    cols="12"
                  >
                    <div>
                      Balance Remaining: ${{
                        appliedGiftCardDetails?.balanceRemaining
                      }}
                    </div>
                  </VCol>

                  <!-- gift card id -->
                  <VCol lg="6" md="6" sm="12" xs="12" cols="12">
                    <VTextField
                      v-model="giftCardId"
                      :rules="[requiredValidator]"
                      :messages="apps.POS?.isVisible ? 'or scan the code' : ''"
                      label="Enter Gift Card Id"
                      @blur="validateGiftCard"
                    />
                  </VCol>

                  <!-- gift card amount -->
                  <VCol lg="6" md="6" sm="12" xs="12" cols="12">
                    <VTextField
                      v-if="props?.type === 'Booking'"
                      v-model="amountToRedeem"
                      :disabled="isInvalidCoupon || !giftCardId"
                      :rules="[
                        requiredValidator,
                        minMaxValidator(
                          amountToRedeem,
                          0.01,
                          Math.min(
                            props?.totalDue,
                            appliedGiftCardDetails?.balanceRemaining || 1,
                          ),
                        ),
                      ]"
                      label="Enter Amount"
                      type="number"
                      prefix="$"
                    />
                    <VTextField
                      v-else
                      v-model="amountToRedeem"
                      :rules="[
                        requiredValidator,
                        minValidator(
                          amountToRedeem,
                          props?.requestedPaymentDetails?.amount,
                        ),
                        decimalRule,
                      ]"
                      label="Enter Amount"
                      type="number"
                      prefix="$"
                    />
                  </VCol>
                </VRow>
              </VForm>

              <!-- gift card apply button -->
              <VCardActions class="d-flex justify-end mt-5 px-0">
                <VBtn
                  v-if="props?.type === 'Booking'"
                  variant="flat"
                  color="primary"
                  @click.prevent="checkBookingBalance('GiftCard')"
                >
                  Apply gift card
                </VBtn>
                <VBtn
                  v-else
                  :disabled="
                    isInvalidCoupon ||
                    !giftCardId ||
                    appliedGiftCardDetails?.balanceRemaining <
                      props?.requestedPaymentDetails?.amount
                  "
                  variant="flat"
                  color="primary"
                  @click.prevent="checkBookingBalance('GiftCard')"
                >
                  Apply gift card
                </VBtn>
              </VCardActions>
            </VCardText>
          </VWindowItem>

          <!-- waive fee -->
          <VWindowItem v-if="props?.haveCreatePermission" value="waiveFee">
            <VCardText class="pt-0 pb-0">
              <VCardTitle class="d-flex justify-left px-0 pt-0">
                Waive Fee
              </VCardTitle>
              <div class="mb-2">Allows you waive the fee for the booking.</div>
              <VForm ref="refVFormWaive" class="mt-4">
                <VRow>
                  <VCol lg="6" md="8" sm="10" xs="12" cols="12">
                    <VTextarea
                      v-model="feeWaiveReason"
                      :rules="[
                        requiredValidator,
                        customRegexValidator(
                          feeWaiveReason,
                          '^.{4,}$',
                          'Please enter minimum of four characters',
                        ),
                      ]"
                      rows="2"
                      label="Reason for Waiving the fee"
                      class="waiveFee_textarea"
                    />
                  </VCol>
                </VRow>
              </VForm>
              <VCardActions class="d-flex justify-end px-0 mt-5">
                <VBtn
                  variant="flat"
                  color="primary"
                  @click.prevent="checkBookingBalance('WaiveFee')"
                >
                  Waive Fee
                </VBtn>
              </VCardActions>
            </VCardText>
          </VWindowItem>

          <!-- pay in store  -->
          <VWindowItem value="payInStore">
            <VCardText class="pt-0 pb-0">
              <VCardTitle class="d-flex justify-left px-0 pt-0">
                Pay in Store
              </VCardTitle>
              <div>Use this option to pay at the venue when you arrive.</div>
              <VCardActions class="d-flex justify-end mt-5 px-0">
                <VBtn
                  variant="flat"
                  color="primary"
                  class="mt-16"
                  @click.prevent="
                    async () => {
                      await confirmBooking();
                      if (props.loggedInUserId || props.isQuickBooking) {
                        emit('closeDialog', false);
                      } else {
                        emit('bookingChange', {
                          bookingId: props?.bookingId,
                          payLater: true,
                        });
                      }
                    }">
                  Confirm - Pay later
                </VBtn>
              </VCardActions>
            </VCardText>
          </VWindowItem>

          <!-- credit card  -->
          <VWindowItem value="creditCard">
            <VCardText class="pt-0 pb-0">
              <VCardTitle class="d-flex justify-left px-0 pt-0">
                Credit Card
              </VCardTitle>
              <div v-if="props?.loggedInUserId || (props?.owner == pb?.authStore?.model?.id)">
                Use this option to pay online with your credit card.
              </div>
              <VForm ref="refVFormCreditCard">
                <!-- user credit cards -->
                <div class="my-4" v-if="props?.loggedInUserId || (props?.owner == pb?.authStore?.model?.id)">
                  <VSelect
                    @update:menu="onUpdateMenu"
                    v-model="selectedCreditCard"
                    :items="[
                      ...props?.userCreditCards,
                      {
                        id: 'newCard',
                        cardNum: 'New Card',
                        name: '',
                        exp: '',
                        token: 'newCard',
                      },
                    ]"
                    :rules="[requiredValidator]"
                    item-title="cardNum"
                    label="Cards"
                    item-value="token"
                    placeholder="Select a card"
                  >
                    <template v-slot:item="{ props, item }">
                      <VListItem
                        v-bind="props"
                        :prepend-avatar="getCardProviders(item.raw?.provider)"
                        :subtitle="
                          item.raw.name && item.raw.exp
                            ? item.raw.name + ' | exp - ' + item.raw?.exp
                            : ''
                        "
                      />
                    </template>
                  </VSelect>
                </div>
              </VForm>

              <!-- pay now button -->
              <VCardActions
                class="d-flex px-0"
                :class="
                  props?.loggedInUserId
                    ? 'justify-space-between'
                    : 'justify-end mt-16'
                "
              >
                <VCheckbox
                  v-if="props?.loggedInUserId && selectedCreditCard == 'newCard'"
                  v-model="saveNewCard"
                  :disabled="selectedCreditCard !== 'newCard'"
                  label="Save Card for future use"
                /><VSpacer v-else></VSpacer>

                <VBtn
                  variant="flat"
                  color="primary"
                  @click.prevent="checkBookingBalance('CreditCard')"
                >
                  Pay Now
                </VBtn>
              </VCardActions>
            </VCardText>
          </VWindowItem>
        </VWindow>
        <div
          v-if="
            props?.type &&
            ['event', 'Booking']?.includes(props?.type) &&
            !['payInStore', 'pos', 'waiveFee']?.includes(currentTab)
          "
        >
          <DiscountApply
            :docket="docketId"
            @applyDiscount="handleDiscountCode"
          />
        </div>
      </VCol>
    </VRow>

    <CardProcessingDialog
      :is-add-card-dialog-visible="isProcessingCardDialogVisible"
      :add-card-dialog-message="addCardDialogMessage"
      :total-amount="props?.totalDue"
      :surcharge-amount="getSurchargeAmount"
      @close-dialog="closeProcessingCardDialog"
    />

    <AddToMyTabDialog
      :is-dialog-visible="isAddToMyTabDialogVisible"
      :user="props?.owner ? props?.owner : ''"
      @addToMyTab="(val) => handleAddToMyTab(val)"
      @closeAddToMyTabDialog="(val: any) => (isAddToMyTabDialogVisible = val)"
    />

    <VDialog
      :model-value="showPreventBenefits"
      persistent
      max-width="700"
      max-height="800"
      no-click-animation>
      <VCard>
        <div
          class="d-flex align-center justify-space-between bg-dialogHeaderColor">
          <VCardTitle> All membership benefits are currently on hold </VCardTitle>
          <VBtn
            icon="mdi-close"
            size="large"
            variant="text"
            class="rounded-0"
            color=""
            @click="showPreventBenefits = false"
          />
        </div>
        <v-card-text>
          <p>There is an issue with your membership and we have been unable to processes your membership payment.</p>

          <v-alert color="error">Please pay the outstanding amount, and confirm your payment methods in your profile for future payments.</v-alert>
        </v-card-text>
        <v-card-actions>
          <v-btn color="secondary" variant="text" @click.prevent="showPreventBenefits = false">Close</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="text" @click.prevent="goToProfile()">Add Credit to Profile</v-btn>
        </v-card-actions>
      </VCard>
    </VDialog>

  </div>
</template>

<style scoped lang="scss">
.waiveFee_textarea {
  textarea {
    scrollbar-width: thin;
  }
}

.info-button-container {
  position: absolute;
  z-index: 10;
  inset-block-start: 0;
  inset-inline-end: 0;
}

.info-icon {
  cursor: pointer;
}

:global(.payment_info_tooltip) {
  max-inline-size: 250px !important;
}

.tooltip-content ul {
  margin: 0;
  padding-inline-start: 20px;
}

.tooltip-content li {
  list-style-type: disc;
  margin-block-end: 4px;
}

.tooltip-content strong {
  font-weight: bold;
}

.tooltip-content span {
  display: block;
  margin-block-end: 8px;
}

.divider-absolute {
  position: absolute;
  z-index: 1;
  block-size: 100%;
  inset-block-start: 0;
  inset-inline-end: -1px;
}
</style>
